import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Sparkles } from "lucide-react";

import { metricsApi, type DailyAgg, type MetricsHourly } from "@/api/metrics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CostCurveWidget } from "@/components/CostCurveWidget";
import { fmt, hourFromTs, num, rangeFor, todayIso, type Period } from "@/lib/format";
import { cn } from "@/lib/utils";

type ViewMode = "pair" | "plan" | "fact" | "delta";
type RangeMode = "day" | Period;

const hourLabel = (ts: string): string => String(hourFromTs(ts)).padStart(2, "0");

/** Зсунути ISO-дату на N днів (повертає YYYY-MM-DD). */
function shiftDate(iso: string, deltaDays: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

export function HomePage1() {
  const { t } = useTranslation();
  const [date, setDate] = useState(todayIso());
  const [range, setRange] = useState<RangeMode>("day");
  const [view, setView] = useState<ViewMode>("pair");

  // hourly — лише в режимі "day"; daily — для решти.
  const hourly = useQuery({
    queryKey: ["metrics-hourly", date],
    queryFn: () => metricsApi.hourly(date),
    enabled: range === "day",
  });
  const dailyRange = useMemo(
    () => (range === "day" ? null : rangeFor(date, range)),
    [range, date],
  );
  const daily = useQuery({
    queryKey: ["metrics-daily-h1", dailyRange?.from, dailyRange?.to],
    queryFn: () => metricsApi.daily(dailyRange!.from, dailyRange!.to),
    enabled: dailyRange !== null,
  });

  const isLoading = range === "day" ? hourly.isLoading : daily.isLoading;
  const hourlyRows = hourly.data ?? [];
  const dailyRows = daily.data ?? [];
  const hasData = range === "day" ? hourlyRows.length > 0 : dailyRows.length > 0;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>{t("home1.title")}</CardTitle>
          <CardDescription>{t("home1.subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          {/* День: input + стрілки prev/next */}
          <div className="space-y-1">
            <Label>{t("plan.date")}</Label>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDate((d) => shiftDate(d, -1))}
                aria-label={t("home1.prev_day")}
                title={t("home1.prev_day")}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-[160px]"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDate((d) => shiftDate(d, 1))}
                aria-label={t("home1.next_day")}
                title={t("home1.next_day")}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Range mode */}
          <div className="space-y-1">
            <Label>&nbsp;</Label>
            <div className="flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
              {(["day", "week", "month", "quarter", "year"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setRange(m)}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm transition-colors",
                    range === m ? "bg-white shadow-sm font-medium" : "text-slate-600 hover:text-slate-900",
                  )}
                >
                  {t(`home1.range_${m}`)}
                </button>
              ))}
            </div>
          </div>

          {/* View mode (pair/plan/fact/delta) */}
          <div className="space-y-1">
            <Label>&nbsp;</Label>
            <div className="flex flex-wrap gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1">
              {(["pair", "plan", "fact", "delta"] as const).map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setView(m)}
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm transition-colors",
                    view === m ? "bg-white shadow-sm font-medium" : "text-slate-600 hover:text-slate-900",
                  )}
                >
                  {t(`home1.view_${m}`)}
                </button>
              ))}
            </div>
          </div>

          <div className="ml-auto">
            <Link to="/optimizer" title={t("home1.open_optimizer")} aria-label={t("home1.open_optimizer")}>
              <Button variant="outline" size="icon">
                <Sparkles className="h-4 w-4" />
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 text-sm text-slate-500">{t("common.loading")}</div>
          ) : !hasData ? (
            <div className="p-6 text-sm text-slate-500">{t("home1.no_data")}</div>
          ) : range === "day" ? (
            <HourTable rows={hourlyRows} view={view} />
          ) : (
            <DayTable rows={dailyRows} view={view} />
          )}
        </CardContent>
      </Card>

      {range === "day" && <CostCurveWidget date={date} />}
    </div>
  );
}

// ────────────────────────────── Hourly table ─────────────────────────────

interface HourTableProps {
  rows: MetricsHourly[];
  view: ViewMode;
}

function HourTable({ rows, view }: HourTableProps) {
  const { t } = useTranslation();

  const totals = useMemo(() => {
    let planRev = 0,
      factRev = 0,
      planProf = 0,
      factProf = 0,
      planEko = 0,
      factEko = 0,
      planRdn = 0,
      factRdn = 0;
    for (const r of rows) {
      planRev += num(r.plan_revenue_uah) ?? 0;
      factRev += num(r.fact_revenue_uah) ?? 0;
      planProf += num(r.plan_profit_uah) ?? 0;
      factProf += num(r.fact_profit_uah) ?? 0;
      planEko += num(r.plan_volumes?.ekosfera ?? null) ?? 0;
      factEko += num(r.fact_volumes?.ekosfera ?? null) ?? 0;
      planRdn += num(r.plan_volumes?.rdn ?? null) ?? 0;
      factRdn += num(r.fact_volumes?.rdn ?? null) ?? 0;
    }
    return { planRev, factRev, planProf, factProf, planEko, factEko, planRdn, factRdn };
  }, [rows]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr className="text-left text-slate-500">
            <th className="p-2 w-16">{t("common.hour")}</th>
            <Th view={view} label={t("home1.power_kw")} />
            <Th view={view} label={t("home1.volume_ekosfera")} />
            <Th view={view} label={t("home1.volume_rdn")} />
            <Th view={view} label={t("home1.revenue")} />
            <Th view={view} label={t("home1.profit")} />
            <th className="p-2 text-right">{t("home1.imbalance")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.ts}
              className={
                "border-t border-slate-100 " +
                (r.had_alarm ? "bg-red-50" : r.had_warning ? "bg-amber-50" : "")
              }
            >
              <td className="p-2 font-mono">
                <span className="flex items-center gap-1">
                  {hourLabel(r.ts)}
                  {r.direction_anomaly && (
                    <span title="FR-METER-DIR: аномалія напрямку лічильника" className="text-amber-600">⚠</span>
                  )}
                  {r.had_alarm && <span title="ГГУ Аварія" className="text-red-700">🚨</span>}
                  {!r.had_alarm && r.had_warning && (
                    <span title="ГГУ Попередження" className="text-amber-700">⚡</span>
                  )}
                </span>
              </td>
              <Cell view={view} plan={num(r.plan_power_kw)} fact={num(r.fact_power_kw)} />
              <Cell
                view={view}
                plan={num(r.plan_volumes?.ekosfera ?? null)}
                fact={num(r.fact_volumes?.ekosfera ?? null)}
              />
              <Cell
                view={view}
                plan={num(r.plan_volumes?.rdn ?? null)}
                fact={num(r.fact_volumes?.rdn ?? null)}
              />
              <Cell view={view} plan={num(r.plan_revenue_uah)} fact={num(r.fact_revenue_uah)} />
              <Cell view={view} plan={num(r.plan_profit_uah)} fact={num(r.fact_profit_uah)} />
              <td className="p-2 text-right font-mono">{fmt(num(r.imbalance_kwh))}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-slate-50 font-medium">
          <tr>
            <td className="p-2">{t("common.total")}</td>
            <td className="p-2" />
            <SumCell view={view} plan={totals.planEko} fact={totals.factEko} />
            <SumCell view={view} plan={totals.planRdn} fact={totals.factRdn} />
            <SumCell view={view} plan={totals.planRev} fact={totals.factRev} />
            <SumCell view={view} plan={totals.planProf} fact={totals.factProf} />
            <td className="p-2" />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ────────────────────────────── Daily table ──────────────────────────────

interface DayTableProps {
  rows: DailyAgg[];
  view: ViewMode;
}

function DayTable({ rows, view }: DayTableProps) {
  const { t } = useTranslation();
  const totals = useMemo(() => {
    let planRev = 0,
      factRev = 0,
      planProf = 0,
      factProf = 0,
      planEko = 0,
      factEko = 0,
      planRdn = 0,
      factRdn = 0,
      factGen = 0;
    for (const r of rows) {
      planRev += num(r.plan_revenue_uah) ?? 0;
      factRev += num(r.fact_revenue_uah) ?? 0;
      planProf += num(r.plan_profit_uah) ?? 0;
      factProf += num(r.fact_profit_uah) ?? 0;
      planEko += num(r.plan_volume_ekosfera) ?? 0;
      factEko += num(r.fact_volume_ekosfera) ?? 0;
      planRdn += num(r.plan_volume_rdn) ?? 0;
      factRdn += num(r.fact_volume_rdn) ?? 0;
      factGen += num(r.fact_generation_kwh) ?? 0;
    }
    return { planRev, factRev, planProf, factProf, planEko, factEko, planRdn, factRdn, factGen };
  }, [rows]);

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr className="text-left text-slate-500">
            <th className="p-2 w-28">{t("home1.col_date")}</th>
            <th className="p-2 text-right">{t("home1.generation_kwh")}</th>
            <Th view={view} label={t("home1.volume_ekosfera")} />
            <Th view={view} label={t("home1.volume_rdn")} />
            <Th view={view} label={t("home1.revenue")} />
            <Th view={view} label={t("home1.profit")} />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.day} className="border-t border-slate-100">
              <td className="p-2 font-mono">{r.day.slice(0, 10)}</td>
              <td className="p-2 text-right font-mono">{fmt(num(r.fact_generation_kwh))}</td>
              <Cell view={view} plan={num(r.plan_volume_ekosfera)} fact={num(r.fact_volume_ekosfera)} />
              <Cell view={view} plan={num(r.plan_volume_rdn)} fact={num(r.fact_volume_rdn)} />
              <Cell view={view} plan={num(r.plan_revenue_uah)} fact={num(r.fact_revenue_uah)} />
              <Cell view={view} plan={num(r.plan_profit_uah)} fact={num(r.fact_profit_uah)} />
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-slate-50 font-medium">
          <tr>
            <td className="p-2">{t("common.total")}</td>
            <td className="p-2 text-right font-mono">{fmt(totals.factGen)}</td>
            <SumCell view={view} plan={totals.planEko} fact={totals.factEko} />
            <SumCell view={view} plan={totals.planRdn} fact={totals.factRdn} />
            <SumCell view={view} plan={totals.planRev} fact={totals.factRev} />
            <SumCell view={view} plan={totals.planProf} fact={totals.factProf} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ──────────────────────────── Shared cells ──────────────────────────────

function Th({ view, label }: { view: ViewMode; label: string }) {
  if (view === "pair") {
    return (
      <th className="p-2 text-right" colSpan={2}>
        {label}
      </th>
    );
  }
  return <th className="p-2 text-right">{label}</th>;
}

function Cell({ view, plan, fact }: { view: ViewMode; plan: number | null; fact: number | null }) {
  if (view === "plan") return <td className="p-2 text-right font-mono">{fmt(plan)}</td>;
  if (view === "fact") return <td className="p-2 text-right font-mono">{fmt(fact)}</td>;
  if (view === "delta") {
    const d = plan !== null && fact !== null ? fact - plan : null;
    return (
      <td
        className={cn(
          "p-2 text-right font-mono",
          d === null ? "" : d > 0 ? "text-emerald-700" : d < 0 ? "text-red-700" : "",
        )}
      >
        {d === null ? "—" : (d > 0 ? "+" : "") + fmt(d)}
      </td>
    );
  }
  // pair
  return (
    <>
      <td className="p-2 text-right font-mono text-slate-500">{fmt(plan)}</td>
      <td className="p-2 text-right font-mono">{fmt(fact)}</td>
    </>
  );
}

function SumCell({ view, plan, fact }: { view: ViewMode; plan: number; fact: number }) {
  if (view === "plan") return <td className="p-2 text-right font-mono">{fmt(plan)}</td>;
  if (view === "fact") return <td className="p-2 text-right font-mono">{fmt(fact)}</td>;
  if (view === "delta") {
    const d = fact - plan;
    return (
      <td
        className={cn(
          "p-2 text-right font-mono",
          d > 0 ? "text-emerald-700" : d < 0 ? "text-red-700" : "",
        )}
      >
        {(d > 0 ? "+" : "") + fmt(d)}
      </td>
    );
  }
  return (
    <>
      <td className="p-2 text-right font-mono text-slate-500">{fmt(plan)}</td>
      <td className="p-2 text-right font-mono">{fmt(fact)}</td>
    </>
  );
}
